import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { TipTapEditor } from '../../components/admin/TipTapEditor';
import { TaxonomySelect } from '../../components/admin/TaxonomySelect';
import { SlugField } from '../../components/admin/SlugField';
import { SEOFields } from '../../components/admin/SEOFields';
import { ReviewNeededBadge, fieldNeedsReview, mapTransformerFieldToFormField } from '../../components/admin/ReviewNeededBadge';
import { API_BASE } from '../../config/api';
import { PageShell } from '../../components/admin/PageShell';
import { FormSection } from '../../components/admin/FormSection';
import { AutoSaveIndicator } from '../../components/admin/AutoSaveIndicator';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { useToast } from '../../hooks/useToast';
import { ToastContainer } from '../../components/admin/Toast';

// Helper: find taxonomy object by slug
const getTaxonomyObjectBySlug = (items = [], slug) => {
  if (!slug) return null;
  return items.find(item => item.slug === slug) || null;
};

export const UniversityForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [generalError, setGeneralError] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const { toasts, success, error: showError, removeToast } = useToast();
  
  const [taxonomies, setTaxonomies] = useState({
    country: [],
    state: [],
    institution_type: [],
    field_of_study: [],
    level_of_study: [],
    mode_of_study: [],
  });

  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    slug: '',
    seo_title: '',
    seo_description: '',
    country: 'malaysia',
    state: '',
    city: '',
    institution_type: '', // Empty by default - will be populated from taxonomy dropdown
    tier: 'B',
    partner_status: 'Normal',
    approval_difficulty: 'Easy',
    registration_flow: "",
    qs_ranking: '',
    setara_rating: '',
    accreditations: [],
    awards: [],
    fields_of_study: [],
    levels_offered: [],
    modes_of_study: [],
    scholarships_available: false,
    cashback_supported: false,
    cashback_rules: '',
    short_description: '',
    full_description: '',
    logo: '',
    campus_gallery: [''],
    brochures: [],
    internal_notes: '',
    featured: false,
    about: { blocks: [] },
    published: false,
    // Contact Details
    contact_email: '',
    contact_whatsapp: '',
    contact_person_name: '',
    contact_person_position: '',
    agent_portal_url: '',
    agent_portal_username: '',
    agent_portal_password: ''
  });

  const [originalSlug, setOriginalSlug] = useState('');
  const [missingFields, setMissingFields] = useState([]); // Track fields that need review from _metadata

  const backendUrl = API_BASE;

  useEffect(() => {
    if (isEdit && id) {
      fetchUniversity();
    }
  }, [id]);

  const fetchUniversity = async () => {
    if (!id) {
      console.error('Cannot fetch university: id is undefined');
      navigate('/admin/universities');
      return;
    }
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/universities/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        navigate('/admin/login');
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch university');

      const data = await response.json();
      const uniData = data.university || data;
      setOriginalSlug(uniData.slug || '');
      
      // Extract metadata for review warnings
      if (uniData._metadata && Array.isArray(uniData._metadata.missing_fields)) {
        // Map transformer field names to form field names
        const mappedMissingFields = uniData._metadata.missing_fields.map(field => 
          mapTransformerFieldToFormField(field, 'university')
        );
        setMissingFields(mappedMissingFields);
      } else {
        setMissingFields([]);
      }
      
      // Note: Taxonomy fields from backend are slugs, but we'll convert them to labels in useEffect
      // after taxonomies are loaded. For now, store slugs as-is.
      setFormData({
        ...uniData,
        slug: uniData.slug || '',
        seo_title: uniData.seo_title || '',
        seo_description: uniData.seo_description || '',
        campus_gallery: uniData.campus_gallery && uniData.campus_gallery.length > 0 ? uniData.campus_gallery : [''],
        accreditations: uniData.accreditations || [],
        awards: uniData.awards || [],
        fields_of_study: Array.isArray(uniData.fields_of_study) ? uniData.fields_of_study : [],
        levels_offered: Array.isArray(uniData.levels_offered) ? uniData.levels_offered : [],
        modes_of_study: Array.isArray(uniData.modes_of_study) ? uniData.modes_of_study : [],
        brochures: uniData.brochures || [],
        internal_notes: uniData.internal_notes || '',
        short_description: uniData.short_description || '',
        full_description: uniData.full_description || '',
        cashback_rules: uniData.cashback_rules || '',
        featured: uniData.featured || false,
        about: typeof uniData.about === 'string' 
          ? { blocks: [] } 
          : (uniData.about || { blocks: [] }),
        // Contact Details
        contact_email: uniData.contact_email || '',
        contact_whatsapp: uniData.contact_whatsapp || '',
        contact_person_name: uniData.contact_person_name || '',
        contact_person_position: uniData.contact_person_position || '',
        agent_portal_url: uniData.agent_portal_url || '',
        agent_portal_username: uniData.agent_portal_username || '',
        agent_portal_password: uniData.agent_portal_password || '',
        // Remove referral_type if present (not in backend model)
        referral_type: undefined
      });
    } catch (err) {
      alert('Failed to load university');
      navigate('/admin/universities');
    } finally {
      setLoading(false);
    }
  };

  // Fetch master taxonomies (country, state)
  useEffect(() => {
    const fetchTaxonomies = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        if (!token) return;

        const res = await fetch(`${API_BASE}/api/admin/master-taxonomy`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          // Set empty taxonomies on error to prevent crashes
          console.error(`Failed to fetch taxonomies: HTTP ${res.status}`);
          setTaxonomies({ country: [], state: [], institution_type: [], field_of_study: [], level_of_study: [], mode_of_study: [] });
          return;
        }

        const data = await res.json();
        
        // Defensive check: ensure data is valid
        if (!data || typeof data !== 'object') {
          console.error('Invalid taxonomy API response format:', data);
          setTaxonomies({ country: [], state: [], institution_type: [], field_of_study: [], level_of_study: [], mode_of_study: [] });
          return;
        }

        const grouped = {
          country: [],
          state: [],
          institution_type: [],
          field_of_study: [],
          level_of_study: [],
          mode_of_study: [],
        };

        // Safely process items
        const items = Array.isArray(data.items) ? data.items : (Array.isArray(data) ? data : []);
        if (!Array.isArray(items)) {
          console.error('Taxonomy items is not an array:', items);
          setTaxonomies({ country: [], state: [], institution_type: [], field_of_study: [], level_of_study: [], mode_of_study: [] });
          return;
        }
        
        items.forEach(item => {
          if (item && item.type && typeof item.type === 'string' && grouped.hasOwnProperty(item.type)) {
            // Only add items with valid slug and label
            if (item.slug && item.label) {
              grouped[item.type].push(item);
            } else {
              console.warn(`Skipping taxonomy item with missing slug/label:`, item);
            }
          }
        });

        // Log counts for debugging
        console.log('Loaded taxonomies:', {
          country: grouped.country.length,
          state: grouped.state.length,
          institution_type: grouped.institution_type.length,
          field_of_study: grouped.field_of_study.length,
          level_of_study: grouped.level_of_study.length,
          mode_of_study: grouped.mode_of_study.length
        });
        
        setTaxonomies(grouped);
      } catch (err) {
        console.error('Failed to load taxonomies', err);
        // Set empty taxonomies on error to prevent crashes
        setTaxonomies({ country: [], state: [], institution_type: [], field_of_study: [], level_of_study: [], mode_of_study: [] });
      }
    };

    fetchTaxonomies();
  }, []);

  // Convert taxonomy slugs to labels when taxonomies are loaded (for edit mode display)
  useEffect(() => {
    if (isEdit && taxonomies.field_of_study.length > 0 && formData.fields_of_study && formData.fields_of_study.length > 0) {
      // Check if values are slugs (not labels) - if they don't match any label, assume they're slugs
      const taxList = taxonomies.field_of_study || [];
      const firstValue = formData.fields_of_study[0];
      const isSlug = taxList.some(t => t.slug === firstValue) && !taxList.some(t => t.label === firstValue);
      
      if (isSlug) {
        // Convert slugs to labels
        const labels = formData.fields_of_study.map(slug => {
          const foundTax = taxList.find(t => t.slug === slug);
          return foundTax ? foundTax.label : slug;
        }).filter(Boolean);
        if (labels.length > 0) {
          setFormData(prev => ({...prev, fields_of_study: labels}));
        }
      }
    }
    
    if (isEdit && taxonomies.level_of_study.length > 0 && formData.levels_offered && formData.levels_offered.length > 0) {
      const taxList = taxonomies.level_of_study || [];
      const firstValue = formData.levels_offered[0];
      const isSlug = taxList.some(t => t.slug === firstValue) && !taxList.some(t => t.label === firstValue);
      
      if (isSlug) {
        const labels = formData.levels_offered.map(slug => {
          const foundTax = taxList.find(t => t.slug === slug);
          return foundTax ? foundTax.label : slug;
        }).filter(Boolean);
        if (labels.length > 0) {
          setFormData(prev => ({...prev, levels_offered: labels}));
        }
      }
    }
    
    if (isEdit && taxonomies.mode_of_study.length > 0 && formData.modes_of_study && formData.modes_of_study.length > 0) {
      const taxList = taxonomies.mode_of_study || [];
      const firstValue = formData.modes_of_study[0];
      const isSlug = taxList.some(t => t.slug === firstValue) && !taxList.some(t => t.label === firstValue);
      
      if (isSlug) {
        const labels = formData.modes_of_study.map(slug => {
          const foundTax = taxList.find(t => t.slug === slug);
          return foundTax ? foundTax.label : slug;
        }).filter(Boolean);
        if (labels.length > 0) {
          setFormData(prev => ({...prev, modes_of_study: labels}));
        }
      }
    }
  }, [taxonomies.field_of_study.length, taxonomies.level_of_study.length, taxonomies.mode_of_study.length, isEdit]);
  
  // Helper: Convert taxonomy labels to slugs
  const convertLabelsToSlugs = (labels, taxonomyType) => {
    if (!Array.isArray(labels) || labels.length === 0) return [];
    const taxList = Array.isArray(taxonomies?.[taxonomyType]) ? (taxonomies[taxonomyType] || []) : [];
    const slugs = labels.map(label => {
      const foundTax = taxList.find(t => t.label === label || t.slug === label);
      return foundTax ? foundTax.slug : label; // Fallback to label if not found (will be validated by backend)
    }).filter(slug => slug); // Remove empty slugs
    return slugs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('admin_token');
      const url = isEdit
        ? `${backendUrl}/api/admin/universities/${id}`
        : `${backendUrl}/api/admin/universities`;

      // Prepare submit data with taxonomy label→slug conversion
      const submitData = {
        ...formData,
        // Convert taxonomy arrays from labels to slugs
        fields_of_study: convertLabelsToSlugs(formData.fields_of_study, 'field_of_study'),
        levels_offered: convertLabelsToSlugs(formData.levels_offered, 'level_of_study'),
        modes_of_study: convertLabelsToSlugs(formData.modes_of_study, 'mode_of_study'),
        // Filter empty values
        campus_gallery: Array.isArray(formData.campus_gallery) ? formData.campus_gallery.filter(url => url.trim() !== '') : [],
        brochures: Array.isArray(formData.brochures) ? formData.brochures.filter(url => url.trim() !== '') : [],
        accreditations: Array.isArray(formData.accreditations) ? formData.accreditations.filter(val => val.trim() !== '') : [],
        awards: Array.isArray(formData.awards) ? formData.awards.filter(val => val.trim() !== '') : [],
        // Ensure commission_structure exists (default empty object)
        commission_structure: formData.commission_structure || {},
        // Convert empty strings to null for optional contact fields
        contact_email: formData.contact_email?.trim() || null,
        contact_whatsapp: formData.contact_whatsapp?.trim() || null,
        contact_person_name: formData.contact_person_name?.trim() || null,
        contact_person_position: formData.contact_person_position?.trim() || null,
        agent_portal_url: formData.agent_portal_url?.trim() || null,
        agent_portal_username: formData.agent_portal_username?.trim() || null,
        agent_portal_password: formData.agent_portal_password?.trim() || null,
        // Remove referral_type (not in backend model)
      };
      delete submitData.referral_type;

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to save university' }));
        
        // Parse error response
        const errors = {};
        let generalError = null;
        
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            // FastAPI validation errors format: [{ loc: ["body", "field"], msg: "message" }, ...]
            errorData.detail.forEach(err => {
              if (err.loc && Array.isArray(err.loc)) {
                const field = err.loc[err.loc.length - 1]; // Get the last element (field name)
                errors[field] = err.msg || 'Invalid value';
              } else {
                generalError = err.msg || 'Validation error';
              }
            });
          } else if (typeof errorData.detail === 'string') {
            // Simple string error (may contain multiple errors separated by semicolons)
            const errorMessages = errorData.detail.split(';').map(e => e.trim()).filter(e => e);
            
            // Try to extract field names from error messages
            errorMessages.forEach(msg => {
              const fieldMatch = msg.match(/(\w+):\s*(.+)/i);
              if (fieldMatch) {
                const field = fieldMatch[1];
                errors[field] = fieldMatch[2];
              } else {
                // Check for common field patterns
                const commonFields = ['name', 'display_name', 'slug', 'country', 'city', 'institution_type', 'fields_of_study', 'levels_offered', 'modes_of_study'];
                const matchedField = commonFields.find(f => msg.toLowerCase().includes(f.replace(/_/g, ' ')));
                if (matchedField) {
                  errors[matchedField] = msg;
                } else {
                  if (!generalError) generalError = msg;
                  else generalError += '; ' + msg;
                }
              }
            });
          }
        } else {
          generalError = 'Failed to save university';
        }
        
        setFieldErrors(errors);
        setGeneralError(generalError);
        showError(generalError || 'Please fix the errors and try again');
        
        // Scroll to top to show errors
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        return; // Don't navigate on error
      }

      // Clear errors on success
      setFieldErrors({});
      setGeneralError(null);
      setLastSaved(new Date());
      success(isEdit ? 'University updated successfully!' : 'University created successfully!');
      
      // Navigate after a short delay to show success message
      setTimeout(() => {
        navigate('/admin/universities');
      }, 1000);
    } catch (err) {
      // Network errors or other exceptions
      const errorMessage = err.message || 'Failed to save university';
      setGeneralError(errorMessage);
      setFieldErrors({});
      console.error('Error saving university:', err);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  const addGalleryImage = () => {
    setFormData(prev => ({
      ...prev,
      campus_gallery: Array.isArray(prev.campus_gallery) ? [...prev.campus_gallery, ""] : [""]
    }));
  };

  const removeGalleryImage = (index) => {
    setFormData(prev => ({
      ...prev,
      campus_gallery: Array.isArray(prev.campus_gallery) 
        ? prev.campus_gallery.filter((_, i) => i !== index)
        : []
    }));
  };

  const updateGalleryImage = (index, value) => {
    setFormData(prev => ({
      ...prev,
      campus_gallery: prev.campus_gallery.map((url, i) => i === index ? value : url)
    }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block w-12 h-12 border-4 border-[#1E40AF] border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-[#6B7280]">Loading university...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <button
            onClick={() => navigate('/admin/universities')}
            className="text-[#1E40AF] hover:text-[#1e3a8a] font-semibold text-sm flex items-center gap-2 mb-3"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Universities
          </button>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
            {isEdit ? 'Edit' : 'Add'} University
          </h1>
        </div>

        {/* Form - Modern Design */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Error Display - Modern */}
          {(generalError || Object.keys(fieldErrors).length > 0) && (
            <div className="bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-200 rounded-xl p-5 shadow-md">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-red-800 mb-2">Please fix the following errors:</h3>
                  {generalError && (
                    <p className="text-sm text-red-700 mb-2">{generalError}</p>
                  )}
                  {Object.keys(fieldErrors).length > 0 && (
                    <ul className="list-disc list-inside space-y-1">
                      {Object.entries(fieldErrors).map(([field, error]) => (
                        <li key={field} className="text-sm text-red-700">
                          <span className="font-medium">{field.replace(/_/g, ' ')}:</span> {error}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Basic Information - Using FormSection */}
          <FormSection
            title="Basic Information"
            subtitle="Core university details"
            icon={
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">
                  Name *
                  <ReviewNeededBadge fieldName="name" missingFields={missingFields} />
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none ${
                    fieldNeedsReview('name', missingFields)
                      ? 'border-amber-300 focus:border-amber-400 bg-amber-50'
                      : 'border-[#E5E7EB] focus:border-[#1E40AF]'
                  }`}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">Display Name</label>
                <input
                  type="text"
                  value={formData.display_name}
                  onChange={(e) => setFormData({...formData, display_name: e.target.value})}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                />
              </div>

              <div className="md:col-span-2">
                <SlugField
                  value={formData.slug}
                  onChange={(slug) => setFormData({...formData, slug})}
                  sourceValue={formData.name}
                  isEdit={isEdit}
                  originalSlug={originalSlug}
                />
              </div>
      
          <div>
            <div className="flex items-center mb-2">
              <label className="block text-sm font-semibold text-[#111827]">
                Country *
                <ReviewNeededBadge fieldName="country" missingFields={missingFields} />
              </label>
            </div>
            <TaxonomySelect
              type="country"
              label=""
              multiple={false}
              value={
                (() => {
                  // TaxonomySelect expects label string, but formData stores slug
                  if (!formData.country) return '';
                  
                  // Safely get taxonomy list
                  const taxList = Array.isArray(taxonomies?.country) ? (taxonomies.country || []) : [];
                  
                  // If no taxonomies loaded yet, return empty to prevent mismatch/crash
                  if (taxList.length === 0) {
                    return '';
                  }
                  
                  // Try to find by slug first (backend stores slugs)
                  const foundBySlug = taxList.find(t => t.slug === formData.country);
                  if (foundBySlug && foundBySlug.label) {
                    return foundBySlug.label;
                  }
                  
                  // Try to find by label (for backward compatibility)
                  const foundByLabel = taxList.find(t => t.label === formData.country);
                  if (foundByLabel && foundByLabel.label) {
                    return foundByLabel.label;
                  }
                  
                  // Not found - return empty to avoid showing incorrect value
                  return '';
                })()
              }
              onChange={(selectedLabel) => {
                // TaxonomySelect with multiple=false passes label string directly (NOT array)
                const labelStr = typeof selectedLabel === 'string' ? selectedLabel : (Array.isArray(selectedLabel) ? selectedLabel[0] : '');
                
                // Safely get taxonomy list
                const taxList = Array.isArray(taxonomies?.country) ? (taxonomies.country || []) : [];
                
                // Convert label back to slug for storage (backend expects taxonomy slug)
                const foundTax = taxList.find(t => t.label === labelStr);
                const slugValue = foundTax ? foundTax.slug : '';
                
                if (!slugValue && labelStr) {
                  console.error(`[UniversityForm] Country label '${labelStr}' not found in taxonomy. Cannot convert to slug.`);
                }
                
                setFormData(prev => ({
                  ...prev,
                  country: slugValue || '',
                  // Reset state if country changes away from Malaysia
                  state: foundTax?.slug === 'malaysia' ? prev.state : ''
                }));
              }}
              placeholder="Select country..."
              required={true}
            />
          </div>


        {formData.country === 'malaysia' && (
        <div>
          <TaxonomySelect
              type="state"
              label="State"
              multiple={false}
              value={
                  (() => {
                    // TaxonomySelect expects label string, but formData stores slug
                    if (!formData.state) return '';
                    
                    // Safely get taxonomy list
                    const taxList = Array.isArray(taxonomies?.state) ? (taxonomies.state || []) : [];
                    
                    // If no taxonomies loaded yet, return empty to prevent mismatch
                    if (taxList.length === 0) {
                      return '';
                    }
                    
                    // Try to find by slug first (backend stores slugs)
                    const foundBySlug = taxList.find(t => t.slug === formData.state);
                    if (foundBySlug && foundBySlug.label) {
                      return foundBySlug.label;
                    }
                    
                    // Try to find by label (for backward compatibility)
                    const foundByLabel = taxList.find(t => t.label === formData.state);
                    if (foundByLabel && foundByLabel.label) {
                      return foundByLabel.label;
                    }
                    
                    // Not found - return empty
                    return '';
                  })()
              }
              onChange={(selectedLabel) => {
                // TaxonomySelect with multiple=false passes label string directly (NOT array)
                const labelStr = typeof selectedLabel === 'string' ? selectedLabel : (Array.isArray(selectedLabel) ? selectedLabel[0] : '');
                
                // Safely get taxonomy list
                const taxList = Array.isArray(taxonomies?.state) ? (taxonomies.state || []) : [];
                
                // Convert label back to slug for storage (backend expects taxonomy slug)
                const foundTax = taxList.find(t => t.label === labelStr);
                const slugValue = foundTax ? foundTax.slug : '';
                
                if (!slugValue && labelStr) {
                  console.error(`[UniversityForm] State label '${labelStr}' not found in taxonomy. Cannot convert to slug.`);
                }
                
                setFormData(prev => ({
                  ...prev,
                  state: slugValue || ''
                }));
              }}
            placeholder="Select state..."
          />
        </div>
      )}

              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                />
              </div>

              <div>
                <div className="flex items-center mb-2">
                  <label className="block text-sm font-semibold text-[#111827]">
                    Institution Type *
                    <ReviewNeededBadge fieldName="institution_type" missingFields={missingFields} />
                  </label>
                </div>
                <TaxonomySelect
                  type="institution_type"
                  label=""
                  value={
                    (() => {
                      // TaxonomySelect expects label string, but formData may have slug
                      if (!formData.institution_type) return '';
                      
                      // Safely get taxonomy list
                      const taxList = Array.isArray(taxonomies?.institution_type) ? (taxonomies.institution_type || []) : [];
                      
                      // If no taxonomies loaded yet, return empty to prevent mismatch
                      if (taxList.length === 0) {
                        return '';
                      }
                      
                      // Try to find by slug first (backend stores slugs)
                      const foundBySlug = taxList.find(t => t.slug === formData.institution_type);
                      if (foundBySlug && foundBySlug.label) return foundBySlug.label;
                      
                      // Try to find by label (for backward compatibility)
                      const foundByLabel = taxList.find(t => t.label === formData.institution_type);
                      if (foundByLabel && foundByLabel.label) return foundByLabel.label;
                      
                      // Not found - return empty to avoid showing incorrect value
                      return '';
                    })()
                  }
                  onChange={(selectedLabel) => {
                    // TaxonomySelect with multiple=false passes label string directly
                    const labelStr = typeof selectedLabel === 'string' ? selectedLabel : (Array.isArray(selectedLabel) ? selectedLabel[0] : '');
                    
                    // Safely get taxonomy list
                    const taxList = Array.isArray(taxonomies?.institution_type) ? (taxonomies.institution_type || []) : [];
                    
                    // Convert label back to slug for storage (backend expects taxonomy slug)
                    const foundTax = taxList.find(t => t.label === labelStr);
                    const slugValue = foundTax ? foundTax.slug : '';
                    
                    if (!slugValue && labelStr) {
                      console.error(`[UniversityForm] Institution type label '${labelStr}' not found in taxonomy. Cannot convert to slug.`);
                    }
                    
                    setFormData(prev => ({...prev, institution_type: slugValue || ''}));
                  }}
                  multiple={false}
                  placeholder="Select institution type..."
                  required={true}
                />
              </div>
            </div>
          </FormSection>

          {/* Classification - Using FormSection */}
          <FormSection
            title="Classification"
            subtitle="University classification and ratings"
            icon={
              <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
            }
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">Tier</label>
                <select
                  value={formData.tier}
                  onChange={(e) => setFormData({...formData, tier: e.target.value})}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                >
                  <option value="A">A</option>
                  <option value="B">B</option>
                  <option value="C">C</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">Partner Status</label>
                <select
                  value={formData.partner_status}
                  onChange={(e) => setFormData({...formData, partner_status: e.target.value})}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                >
                  <option value="Close">Close Partner</option>
                  <option value="Normal">Normal Partner</option>
                  <option value="Not Partner">Not Partner</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">Approval Difficulty</label>
                <select
                  value={formData.approval_difficulty}
                  onChange={(e) => setFormData({...formData, approval_difficulty: e.target.value})}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                >
                  <option value="Easy">Easy</option>
                  <option value="Strict">Strict</option>
                </select>
              </div>

              <div className="form-group">
                <label>
                  Registration Flow <span style={{ color: "red" }}>*</span>
                </label>
                <select
                  value={formData.registration_flow}
                  onChange={(e) =>
                  setFormData({ ...formData, registration_flow: e.target.value })
                  }
                  required
                >
                  <option value="">Select registration flow</option>
                  <option value="DIRECT">Direct Application</option>
                  <option value="PLATFORM_ASSISTED">Platform Assisted</option>
                  <option value="UNIVERSITY_LED">University Managed</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">QS Ranking</label>
                <input
                  type="number"
                  value={formData.qs_ranking}
                  onChange={(e) => setFormData({...formData, qs_ranking: e.target.value})}
                  placeholder="Optional"
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">SETARA Rating</label>
                <input
                  type="text"
                  value={formData.setara_rating}
                  onChange={(e) => setFormData({...formData, setara_rating: e.target.value})}
                  placeholder="Optional"
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                />
              </div>
            </div>
          </FormSection>

          {/* Academic Coverage - Using FormSection */}
          <FormSection
            title="Academic Coverage"
            subtitle="Fields, levels, and modes of study"
            icon={
              <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            }
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <TaxonomySelect
                  type="field_of_study"
                  label="Fields of Study"
                  value={
                    (() => {
                      // Convert slugs to labels for display
                      if (!Array.isArray(formData.fields_of_study) || formData.fields_of_study.length === 0) return [];
                      const taxList = Array.isArray(taxonomies?.field_of_study) ? (taxonomies.field_of_study || []) : [];
                      return formData.fields_of_study.map(slug => {
                        const foundTax = taxList.find(t => t.slug === slug);
                        return foundTax ? foundTax.label : slug; // Fallback to slug if not found
                      });
                    })()
                  }
                  onChange={(labels) => {
                    // TaxonomySelect with multiple=true returns array of labels
                    const labelArray = Array.isArray(labels) ? labels : [];
                    setFormData(prev => ({...prev, fields_of_study: labelArray}));
                  }}
                  multiple={true}
                  placeholder="Select fields of study..."
                />
              </div>

              <div>
                <TaxonomySelect
                  type="level_of_study"
                  label="Levels Offered"
                  value={
                    (() => {
                      // Convert slugs to labels for display
                      if (!Array.isArray(formData.levels_offered) || formData.levels_offered.length === 0) return [];
                      const taxList = Array.isArray(taxonomies?.level_of_study) ? (taxonomies.level_of_study || []) : [];
                      return formData.levels_offered.map(slug => {
                        const foundTax = taxList.find(t => t.slug === slug);
                        return foundTax ? foundTax.label : slug;
                      });
                    })()
                  }
                  onChange={(labels) => {
                    const labelArray = Array.isArray(labels) ? labels : [];
                    setFormData(prev => ({...prev, levels_offered: labelArray}));
                  }}
                  multiple={true}
                  placeholder="Select levels..."
                />
              </div>

              <div>
                <TaxonomySelect
                  type="mode_of_study"
                  label="Modes of Study"
                  value={
                    (() => {
                      // Convert slugs to labels for display
                      if (!Array.isArray(formData.modes_of_study) || formData.modes_of_study.length === 0) return [];
                      const taxList = Array.isArray(taxonomies?.mode_of_study) ? (taxonomies.mode_of_study || []) : [];
                      return formData.modes_of_study.map(slug => {
                        const foundTax = taxList.find(t => t.slug === slug);
                        return foundTax ? foundTax.label : slug;
                      });
                    })()
                  }
                  onChange={(labels) => {
                    const labelArray = Array.isArray(labels) ? labels : [];
                    setFormData(prev => ({...prev, modes_of_study: labelArray}));
                  }}
                  multiple={true}
                  placeholder="Select modes..."
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-[#111827] mb-2">Accreditations (One per line)</label>
                <textarea
                  value={Array.isArray(formData.accreditations) ? formData.accreditations.join('\n') : ''}
                  onChange={(e) => {
                    const lines = e.target.value.split('\n').map(line => line.trim()).filter(line => line);
                    setFormData(prev => ({...prev, accreditations: lines}));
                  }}
                  rows={3}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                  placeholder="ABET&#10;WASC&#10;AACSB"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-[#111827] mb-2">Awards (One per line)</label>
                <textarea
                  value={Array.isArray(formData.awards) ? formData.awards.join('\n') : ''}
                  onChange={(e) => {
                    const lines = e.target.value.split('\n').map(line => line.trim()).filter(line => line);
                    setFormData(prev => ({...prev, awards: lines}));
                  }}
                  rows={3}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                  placeholder="Best University Award&#10;Excellence in Research"
                />
              </div>
            </div>
          </FormSection>

          {/* Features - Using FormSection */}
          <FormSection
            title="Features"
            subtitle="University features and capabilities"
            icon={
              <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
            }
          >
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="scholarships"
                  checked={formData.scholarships_available}
                  onChange={(e) => setFormData({...formData, scholarships_available: e.target.checked})}
                  className="w-5 h-5"
                />
                <label htmlFor="scholarships" className="text-sm font-semibold text-[#111827] cursor-pointer">
                  Scholarships Available
                </label>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="cashback"
                  checked={formData.cashback_supported}
                  onChange={(e) => setFormData({...formData, cashback_supported: e.target.checked})}
                  className="w-5 h-5"
                />
                <label htmlFor="cashback" className="text-sm font-semibold text-[#111827] cursor-pointer">
                  Cashback Supported
                </label>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="featured"
                  checked={formData.featured}
                  onChange={(e) => setFormData({...formData, featured: e.target.checked})}
                  className="w-5 h-5"
                />
                <label htmlFor="featured" className="text-sm font-semibold text-[#111827] cursor-pointer">
                  Featured University
                </label>
              </div>

              {formData.cashback_supported && (
                <div className="ml-8">
                  <label className="block text-sm font-semibold text-[#111827] mb-2">Cashback Rules</label>
                  <textarea
                    value={formData.cashback_rules || ''}
                    onChange={(e) => setFormData({...formData, cashback_rules: e.target.value})}
                    rows={3}
                    className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                    placeholder="Describe cashback terms and conditions..."
                  />
                </div>
              )}
            </div>
          </FormSection>

          {/* Media - Using FormSection */}
          <FormSection
            title="Media"
            subtitle="Logos, images, and documents"
            icon={
              <svg className="w-5 h-5 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 18h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            }
          >
            <h2 className="text-xl font-bold text-gray-900 mb-5 flex items-center gap-2 pb-3 border-b border-gray-200">
              <div className="w-1 h-6 bg-gradient-to-b from-cyan-600 to-blue-600 rounded-full"></div>
              Media
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">Logo URL</label>
                <input
                  type="url"
                  value={formData.logo}
                  onChange={(e) => setFormData({...formData, logo: e.target.value})}
                  placeholder="https://..."
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">Campus Gallery URLs</label>
                {Array.isArray(formData.campus_gallery) && formData.campus_gallery.map((url, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="url"
                      value={url}
                      onChange={(e) => updateGalleryImage(index, e.target.value)}
                      placeholder="https://..."
                      className="flex-1 px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                    />
                    {Array.isArray(formData.campus_gallery) && formData.campus_gallery.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeGalleryImage(index)}
                        className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addGalleryImage}
                  className="text-[#1E40AF] hover:text-[#1e3a8a] font-semibold text-sm flex items-center gap-2 mt-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Image
                </button>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">Brochure URLs</label>
                {Array.isArray(formData.brochures) && formData.brochures.map((url, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="url"
                      value={url}
                      onChange={(e) => {
                        const newBrochures = [...formData.brochures];
                        newBrochures[index] = e.target.value;
                        setFormData(prev => ({...prev, brochures: newBrochures}));
                      }}
                      placeholder="https://..."
                      className="flex-1 px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newBrochures = formData.brochures.filter((_, i) => i !== index);
                        setFormData(prev => ({...prev, brochures: newBrochures.length > 0 ? newBrochures : []}));
                      }}
                      className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    const newBrochures = Array.isArray(formData.brochures) ? [...formData.brochures, ''] : [''];
                    setFormData(prev => ({...prev, brochures: newBrochures}));
                  }}
                  className="text-[#1E40AF] hover:text-[#1e3a8a] font-semibold text-sm flex items-center gap-2 mt-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Brochure URL
                </button>
              </div>
            </div>
          </FormSection>

          {/* Content - Using FormSection */}
          <FormSection
            title="Content"
            subtitle="Descriptions and rich content"
            icon={
              <svg className="w-5 h-5 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }
          >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">
                  Short Description
                  <ReviewNeededBadge fieldName="short_description" missingFields={missingFields} />
                </label>
                <textarea
                  value={formData.short_description || ''}
                  onChange={(e) => setFormData({...formData, short_description: e.target.value})}
                  rows={3}
                  className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none ${
                    fieldNeedsReview('short_description', missingFields)
                      ? 'border-amber-300 focus:border-amber-400 bg-amber-50'
                      : 'border-[#E5E7EB] focus:border-[#1E40AF]'
                  }`}
                  placeholder="Brief description (1-2 sentences) shown in listings..."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">Full Description</label>
                <textarea
                  value={formData.full_description || ''}
                  onChange={(e) => setFormData({...formData, full_description: e.target.value})}
                  rows={6}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                  placeholder="Comprehensive description (supports HTML)..."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">
                  About (Rich Text)
                  <ReviewNeededBadge fieldName="about" missingFields={missingFields} />
                </label>
                <div className={fieldNeedsReview('about', missingFields) ? 'border-2 border-amber-300 rounded-lg p-1 bg-amber-50' : ''}>
                  <TipTapEditor
                    content={formData.about}
                    onChange={(data) => setFormData({...formData, about: data})}
                    placeholder="About the university..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">Internal Notes</label>
                <textarea
                  value={formData.internal_notes || ''}
                  onChange={(e) => setFormData({...formData, internal_notes: e.target.value})}
                  rows={4}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                  placeholder="Internal admin notes (not visible to public)..."
                />
                <p className="text-xs text-[#6B7280] mt-1">These notes are only visible to admins and are not published.</p>
              </div>
            </div>
          </FormSection>

          {/* Contact Details - Using FormSection */}
          <FormSection
            title="Contact Details"
            subtitle="Contact information for the university's person-in-charge. All fields are optional."
            icon={
              <svg className="w-5 h-5 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            }
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">Contact Email</label>
                <input
                  type="email"
                  value={formData.contact_email || ''}
                  onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                  placeholder="person@university.edu"
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                />
                <p className="text-xs text-[#6B7280] mt-1">Email of person-in-charge or communication email</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">WhatsApp Mobile Number</label>
                <input
                  type="tel"
                  value={formData.contact_whatsapp || ''}
                  onChange={(e) => setFormData({...formData, contact_whatsapp: e.target.value})}
                  placeholder="+60123456789"
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">Key Person's Name</label>
                <input
                  type="text"
                  value={formData.contact_person_name || ''}
                  onChange={(e) => setFormData({...formData, contact_person_name: e.target.value})}
                  placeholder="John Doe"
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">Position</label>
                <input
                  type="text"
                  value={formData.contact_person_position || ''}
                  onChange={(e) => setFormData({...formData, contact_person_position: e.target.value})}
                  placeholder="Admissions Manager"
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                />
              </div>
            </div>
          </FormSection>

          {/* Agent Portal Details - Using FormSection */}
          <FormSection
            title="Agent Portal Details"
            subtitle="If the university has an agent portal, provide the access details below. All fields are optional."
            icon={
              <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            }
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-[#111827] mb-2">Agent Portal URL</label>
                <input
                  type="url"
                  value={formData.agent_portal_url || ''}
                  onChange={(e) => setFormData({...formData, agent_portal_url: e.target.value})}
                  placeholder="https://portal.university.edu"
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">Username</label>
                <input
                  type="text"
                  value={formData.agent_portal_username || ''}
                  onChange={(e) => setFormData({...formData, agent_portal_username: e.target.value})}
                  placeholder="portal_username"
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">Password</label>
                <input
                  type="password"
                  value={formData.agent_portal_password || ''}
                  onChange={(e) => setFormData({...formData, agent_portal_password: e.target.value})}
                  placeholder="••••••••"
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                />
                <p className="text-xs text-[#6B7280] mt-1">Password will be stored securely</p>
              </div>
            </div>
          </FormSection>

          {/* SEO Settings - Using FormSection */}
          <FormSection
            title="SEO Settings"
            subtitle="Search engine optimization metadata"
            icon={
              <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            }
          >
            <SEOFields
              seoData={{ seo_title: formData.seo_title, seo_description: formData.seo_description }}
              onChange={(seoData) => setFormData({...formData, ...seoData})}
              missingFields={missingFields}
            />
          </FormSection>

          {/* Publish Status - Using FormSection */}
          <FormSection
            title="Publish Settings"
            subtitle="Control visibility on the website"
            icon={
              <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            }
          >
            <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <input
                type="checkbox"
                id="published"
                checked={formData.published}
                onChange={(e) => setFormData({...formData, published: e.target.checked})}
                className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <label htmlFor="published" className="text-sm font-semibold text-gray-900 cursor-pointer">
                Publish (visible on website)
              </label>
            </div>
          </FormSection>

          {/* Submit - Modern Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t-2 border-gray-200">
            <button
              type="submit"
              disabled={saving}
              className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {isEdit ? 'Update' : 'Create'} University
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/universities')}
              className="px-8 py-3 bg-white border-2 border-gray-300 text-gray-700 hover:border-gray-400 rounded-lg font-semibold transition-all"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default UniversityForm;
